/* Patchistry expression hero: one responsive video and explicit playback intent. */
(() => {
  'use strict';

  const runtimeKey = '__patchistryExpressionHeroRuntime';
  if (window[runtimeKey]) {
    window[runtimeKey].scan(document);
    return;
  }

  const instances = new Map();
  const playbackTimeout = 20000;

  function createController(root) {
    const video = root.querySelector('video[data-hero-video]');
    if (!video) return null;

    const toggle = root.querySelector('[data-hero-toggle]');
    const replay = root.querySelector('[data-hero-replay]');
    const toggleLabel = root.querySelector('[data-hero-toggle-label]');
    const status = root.querySelector('[data-hero-status]');
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const cleanups = [];

    let destroyed = false;
    let inView = false;
    let pageActive = true;
    let intent = 'auto'; // Auto, explicit play, and explicit pause are separate from visibility.
    let state = 'paused';
    let selection = { src: '', poster: '', mode: 'desktop' };
    let sourceAttached = false;
    let failed = false;
    let autoplayBlocked = false;
    let pendingAction = null;
    let operation = null;
    let bufferTimer = null;
    let expectedPauses = 0;
    let previousReducedMotion = motionQuery.matches;
    let previousSaveData = Boolean(connection && connection.saveData);

    function listen(target, name, callback, options) {
      target.addEventListener(name, callback, options);
      cleanups.push(() => target.removeEventListener(name, callback, options));
    }

    function listenQuery(query, callback) {
      if (query.addEventListener) listen(query, 'change', callback);
      else {
        query.addListener(callback);
        cleanups.push(() => query.removeListener(callback));
      }
    }

    function wantsPlayback() {
      return intent === 'play' || (intent === 'auto' && !motionQuery.matches && !(connection && connection.saveData));
    }

    function canPlay() {
      return !destroyed && pageActive && !document.hidden && inView && wantsPlayback() && !failed && !autoplayBlocked;
    }

    function updateState(nextState) {
      state = nextState;
      root.dataset.videoState = nextState;
      const pauseAction = nextState === 'playing' || (nextState === 'loading' && wantsPlayback());
      const label = pauseAction ? root.dataset.labelPause : root.dataset.labelPlay;
      if (toggle) toggle.setAttribute('aria-label', label || '');
      if (toggleLabel) toggleLabel.textContent = label || '';
      video.setAttribute('aria-busy', nextState === 'loading' ? 'true' : 'false');
      if (status) status.textContent = nextState === 'error' ? (root.dataset.labelError || '') : '';
    }

    function clearBufferTimer() {
      if (bufferTimer !== null) window.clearTimeout(bufferTimer);
      bufferTimer = null;
    }

    function cancelOperation() {
      const previous = operation;
      operation = null;
      if (!previous) return;
      window.clearTimeout(previous.timer);
      previous.controller.abort();
    }

    function pauseInternally() {
      // The pause event is asynchronous; retain its cause until it arrives.
      if (!video.paused) expectedPauses += 1;
      video.pause();
    }

    function suspend() {
      cancelOperation();
      clearBufferTimer();
      pauseInternally();
      if (!failed) updateState('paused');
    }

    function releaseSource() {
      pauseInternally();
      video.removeAttribute('src');
      video.preload = 'none';
      sourceAttached = false;
      // Reset decoded frames so the selected poster is visible after an error.
      try { video.load(); } catch (_) { /* The poster and primary link remain usable. */ }
    }

    function failPlayback() {
      if (destroyed) return;
      failed = true;
      pendingAction = null;
      cancelOperation();
      clearBufferTimer();
      releaseSource();
      updateState('error');
    }

    function armBufferTimer() {
      if (bufferTimer !== null || !canPlay()) return;
      bufferTimer = window.setTimeout(() => {
        bufferTimer = null;
        if (canPlay()) failPlayback();
      }, playbackTimeout);
    }

    function abortError() {
      const error = new Error();
      error.name = 'AbortError';
      return error;
    }

    function waitForMetadata(signal) {
      if (signal.aborted) return Promise.reject(abortError());
      if (video.readyState >= 1) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const cleanup = () => {
          video.removeEventListener('loadedmetadata', ready);
          signal.removeEventListener('abort', aborted);
        };
        const ready = () => { cleanup(); resolve(); };
        const aborted = () => { cleanup(); reject(abortError()); };
        video.addEventListener('loadedmetadata', ready, { once: true });
        signal.addEventListener('abort', aborted, { once: true });
      });
    }

    function waitForPlay(promise, signal) {
      if (signal.aborted) return Promise.reject(abortError());
      return new Promise((resolve, reject) => {
        const aborted = () => reject(abortError());
        signal.addEventListener('abort', aborted, { once: true });
        Promise.resolve(promise).then(
          () => { signal.removeEventListener('abort', aborted); resolve(); },
          error => { signal.removeEventListener('abort', aborted); reject(error); }
        );
      });
    }

    async function beginPlayback() {
      if (operation || !canPlay() || !selection.src) return;
      const current = { controller: new AbortController(), timer: null, timedOut: false };
      operation = current;
      current.timer = window.setTimeout(() => {
        if (operation !== current) return;
        current.timedOut = true;
        current.controller.abort();
      }, playbackTimeout);
      updateState('loading');

      try {
        video.muted = true;
        video.preload = 'auto';
        if (!sourceAttached) {
          video.src = selection.src;
          sourceAttached = true;
          video.load();
        }
        await waitForMetadata(current.controller.signal);
        if (operation !== current || !canPlay()) return;

        if (pendingAction !== null) {
          video.currentTime = 0;
          pendingAction = null;
        }

        await waitForPlay(video.play(), current.controller.signal);
        if (operation === current && canPlay()) updateState(video.paused ? 'paused' : 'playing');
      } catch (error) {
        if (destroyed || operation !== current) return;
        if (!current.timedOut && error && (error.name === 'NotAllowedError' || error.name === 'AbortError')) {
          // A browser autoplay refusal requires a new explicit play action, not repeated retries.
          autoplayBlocked = true;
          clearBufferTimer();
          pauseInternally();
          updateState('paused');
        } else failPlayback();
      } finally {
        window.clearTimeout(current.timer);
        if (operation === current) operation = null;
      }
    }

    function syncPlayback() {
      if (destroyed) return;
      if (!canPlay()) {
        suspend();
        return;
      }
      if (!video.paused && pendingAction === null) return;
      void beginPlayback();
    }

    function requestPlay(action) {
      intent = 'play';
      failed = false;
      autoplayBlocked = false;
      pendingAction = action || (video.ended ? 'replay' : null);
      cancelOperation();
      clearBufferTimer();
      syncPlayback();
    }

    function refresh() {
      if (destroyed) return;
      const preferred = mobileQuery.matches ? 'mobile' : 'desktop';
      const alternate = preferred === 'mobile' ? 'desktop' : 'mobile';
      const sourceFor = mode => (root.getAttribute(`data-${mode}-src`) || '').trim();
      const mode = sourceFor(preferred) ? preferred : alternate;
      const next = {
        mode,
        src: sourceFor(mode),
        poster: root.getAttribute(`data-${mode}-poster`) || ''
      };
      const changed = next.src !== selection.src;
      selection = next;
      if (next.poster) video.poster = next.poster;
      else video.removeAttribute('poster');
      [toggle, replay].forEach(button => { if (button) button.hidden = !next.src; });

      if (changed) {
        cancelOperation();
        clearBufferTimer();
        releaseSource();
        failed = false;
        updateState('paused');
      } else updateState(state);
      if (!next.src) failPlayback();
      else syncPlayback();
    }

    function preferencesChanged() {
      const reducedMotion = motionQuery.matches;
      const saveData = Boolean(connection && connection.saveData);
      // A newly enabled restriction pauses until the next explicit play request.
      if ((!previousReducedMotion && reducedMotion) || (!previousSaveData && saveData)) {
        if (intent !== 'pause') intent = 'auto';
      }
      previousReducedMotion = reducedMotion;
      previousSaveData = saveData;
      syncPlayback();
    }

    if (toggle) listen(toggle, 'click', () => {
      if (state === 'playing' || (state === 'loading' && wantsPlayback())) {
        intent = 'pause';
        pendingAction = null;
        suspend();
      } else requestPlay(null);
    });
    if (replay) listen(replay, 'click', () => requestPlay('replay'));

    listen(video, 'playing', () => {
      if (!canPlay()) { suspend(); return; }
      clearBufferTimer();
      updateState('playing');
    });
    listen(video, 'pause', () => {
      if (destroyed) return;
      if (expectedPauses > 0) expectedPauses -= 1;
      else if (pageActive && !document.hidden && inView && !failed) {
        // Also honor a pause from browser/OS media controls.
        intent = 'pause';
        pendingAction = null;
        cancelOperation();
      }
      clearBufferTimer();
      if (!failed) updateState('paused');
    });
    const buffering = () => {
      if (!canPlay()) return;
      updateState('loading');
      armBufferTimer();
    };
    listen(video, 'waiting', buffering);
    listen(video, 'stalled', buffering);
    listen(video, 'seeking', buffering);
    const playable = () => {
      if (!canPlay()) return;
      if (!video.paused && !video.seeking && video.readyState >= 3) {
        clearBufferTimer();
        updateState('playing');
      } else if (video.paused && !operation) syncPlayback();
    };
    listen(video, 'canplay', playable);
    listen(video, 'seeked', playable);
    listen(video, 'error', () => { if (video.error) failPlayback(); });
    listen(document, 'visibilitychange', syncPlayback);
    listen(window, 'pagehide', () => { pageActive = false; suspend(); });
    listen(window, 'pageshow', () => { pageActive = true; syncPlayback(); });
    listenQuery(mobileQuery, refresh);
    listenQuery(motionQuery, preferencesChanged);
    if (connection && connection.addEventListener) listen(connection, 'change', preferencesChanged);

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.removeAttribute('autoplay');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        inView = entry.isIntersecting && entry.intersectionRatio >= 0.1;
        syncPlayback();
      }, { threshold: [0, 0.1] });
      observer.observe(video);
      cleanups.push(() => observer.disconnect());
    } else {
      const checkView = () => {
        const rect = video.getBoundingClientRect();
        inView = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
        syncPlayback();
      };
      listen(window, 'scroll', checkView, { passive: true });
      listen(window, 'resize', checkView, { passive: true });
      checkView();
    }

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(refresh);
      observer.observe(root, {
        attributes: true,
        attributeFilter: ['data-desktop-src', 'data-mobile-src', 'data-desktop-poster', 'data-mobile-poster', 'data-label-play', 'data-label-pause', 'data-label-error']
      });
      cleanups.push(() => observer.disconnect());
    }

    refresh();
    return {
      refresh,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        cancelOperation();
        clearBufferTimer();
        cleanups.forEach(cleanup => cleanup());
        releaseSource();
      }
    };
  }

  function scan(scope) {
    instances.forEach((controller, root) => {
      if (!root.isConnected) { controller.destroy(); instances.delete(root); }
    });
    const roots = [];
    if (scope.matches && scope.matches('[data-expression-hero]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-expression-hero]'));
    roots.forEach(root => {
      if (instances.has(root)) instances.get(root).refresh();
      else {
        const controller = createController(root);
        if (controller) instances.set(root, controller);
      }
    });
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((controller, root) => {
      if (event.target === root || (event.target.contains && event.target.contains(root))) {
        controller.destroy();
        instances.delete(root);
      }
    });
  });
  window[runtimeKey] = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
